/**
 * interfaces
 */
import { IUnitTypeModel } from './interfaces';
import { IUnitTypeDto } from '../../interfaces';

/**
 * enums
 */
import { ThemeColorsVariants } from '@core/enums/theme-colors-variants';

/**
 * shared
 */
import { BadgeAppearance, BadgeModel } from '@shared/directives';

/**
 * View model for a single unit type row displayed in the data table.
 *
 * Wraps an {@link IUnitTypeDto} and exposes camelCase getters so templates
 * never touch the raw snake_case DTO directly.
 *
 * ### Lazy caching
 * {@link defaultTypeLevelBadge} allocates a {@link BadgeModel} only on the
 * first call; subsequent calls return the cached instance, avoiding redundant
 * object creation during change-detection cycles.
 *
 * @example
 * ```ts
 * const model = new UnitTypeModel(dto);
 * console.log(model.name);                 // "Apartment"
 * console.log(model.defaultTypeLevelBadge().label); // "GENERAL.YES"
 * ```
 */
export class UnitTypeModel implements IUnitTypeModel {

    /** Lazily initialized badge for the `default_type_for_its_level` column. */
    private _defaultTypeLevelBadge!: BadgeModel<boolean>;

    /**
     * @param dto - Raw unit type object returned by the list API endpoint.
     */
    constructor(public readonly dto: IUnitTypeDto) {}

    /**
     * Unique identifier of the unit type.
     */
    public get id(): string {
        return this.dto.id;
    }

    /**
     * Display name of the unit type.
     */
    public get name(): string {
        return this.dto.name;
    }

    /**
     * Short description of the unit type.
     */
    public get description(): string {
        return this.dto.description;
    }

    /**
     * Hierarchy level this unit type belongs to.
     */
    public get level(): number {
        return this.dto.default_level;
    }

    /**
     * Whether this is the default type for its hierarchy level.
     */
    public get defaultTypeForItsLevel(): boolean {
        return this.dto.default_type_for_its_level;
    }

    /**
     * Icon identifier associated with this unit type, or `null` if not set.
     */
    public get icon(): string | null {
        return this.dto.default_icon;
    }

    /**
     * ISO date string of when this unit type was created.
     */
    public get createdOn(): string {
        return this.dto.created_on;
    }

    /**
     * Returns a lazily-cached badge for the `default_type_for_its_level` column.
     *
     * - **Success (green)** — Yes (default for its level)
     * - **Secondary (grey)** — No
     *
     * The badge is built once and reused on every subsequent call.
     *
     * @returns A {@link BadgeModel} wrapping the boolean value with label and color variant.
     */
    public defaultTypeLevelBadge(): BadgeModel<boolean> {
        if (this._defaultTypeLevelBadge != null) {
            return this._defaultTypeLevelBadge;
        }

        const value   = this.defaultTypeForItsLevel;
        const label   = value ? 'GENERAL.YES' : 'GENERAL.NO';
        const variant = value ? ThemeColorsVariants.Success : ThemeColorsVariants.Secondary;

        this._defaultTypeLevelBadge = new BadgeModel<boolean>(value, label, variant, BadgeAppearance.Label);
        return this._defaultTypeLevelBadge;
    }

}
